const APP_SCHEME = 'playaz4playaz';
const ANDROID_PACKAGE = 'com.playaz4playaz';
const APP_STORE_URL = 'https://apps.apple.com/us/app/playaz4playaz/id1550239863';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.playaz4playaz';
const GET_THE_APP_URL = 'https://playaz4playaz.com/get-the-mobile-app';

// How long to wait for the OS to hand control to the native app before we
// assume the app isn't installed. Long enough to survive the app-switch
// animation, short enough that the store fallback still feels prompt.
const FALLBACK_DELAY = 2000;

// If the fallback timer fires this much later than scheduled, the page's JS was
// suspended while it was in the foreground — which only happens because the
// native "Open in app?" dialog was on screen (Safari blocks JS while a system
// alert is up). That means the app IS installed, so we must not go to the
// store — whether the user tapped Open or Cancel.
const SUSPEND_TOLERANCE = 300;

/**
 * Detects the mobile OS from the user agent: 'ios' | 'android' | 'other'.
 */
export function getMobileOS() {
  const ua = navigator.userAgent || '';

  if (
    (/iPad|iPhone|iPod/.test(ua) && !window.MSStream) ||
    // iPadOS reports as Mac; the touch check disambiguates from a real desktop
    (/Mac/.test(ua) && 'ontouchend' in document)
  ) {
    return 'ios';
  }
  if (/android/i.test(ua)) return 'android';
  return 'other';
}

/**
 * The store the current device should fall back to when the app is missing.
 */
export function getStoreUrl() {
  const os = getMobileOS();
  if (os === 'ios') return APP_STORE_URL;
  if (os === 'android') return PLAY_STORE_URL;
  return GET_THE_APP_URL;
}

/**
 * The app's How to Earn Rewards explainer, where the Welcome email's "Learn How It Works" lands.
 * The same path the app's linking config and the backend's email link use.
 */
export const HOW_TO_EARN_REWARDS_PATH = 'how-to-earn-rewards';

/**
 * Opens the installed app — on its landing screen, or at `appPath` — or the platform store when it
 * is not installed.
 *
 * Android gets an `intent:` URL rather than the bare scheme. Chromium browsers (Chrome, Brave,
 * Samsung Internet) resolve it themselves — the app if the package is installed, otherwise
 * `browser_fallback_url` — so nothing has to guess from a timer, and it is the form those browsers
 * still launch from script where a bare custom scheme may be ignored. iOS has no equivalent, so it
 * takes the scheme-plus-timer route `openMobileAppLink` already uses.
 *
 * The default path is `open`, which no app route matches: the app treats it as "no deep link" and
 * shows its own landing screen, which is what "Open the App" promises. A build that does not know a
 * given `appPath` treats it the same way, so naming a screen never leaves a fan anywhere worse.
 */
export function openAppOrStore(appPath = 'open') {
  const os = getMobileOS();

  if (os === 'android') {
    window.location.href =
      `intent://${appPath}#Intent;scheme=${APP_SCHEME};package=${ANDROID_PACKAGE};` +
      `S.browser_fallback_url=${encodeURIComponent(PLAY_STORE_URL)};end`;
    return;
  }

  if (os === 'ios') {
    openMobileAppLink(appPath);
    return;
  }

  window.location.href = GET_THE_APP_URL;
}

/**
 * Tries to open the installed native app at `targetPath`; if the app isn't
 * installed, falls back to the platform store.
 *
 * Whether the app opened is inferred from two signals, which together behave
 * correctly across Chrome AND Safari (incl. the "Open in app?" dialog):
 *   1. The tab losing visibility (visibilitychange/pagehide) — Chrome and
 *      Safari both background the tab when the app launches.
 *   2. The fallback timer firing much later than scheduled — Safari freezes JS
 *      while its confirmation dialog is up, so a big drift means the dialog
 *      appeared, i.e. the app is installed.
 *
 * Result: Open -> app opens (no store); Cancel -> stays put (dialog froze JS,
 * so the drift check cancels the redirect); app not installed -> store.
 *
 * @param {string|boolean} targetPathOrIsAndroid deeplink path, or legacy isAndroid flag
 * @param {boolean} legacyIsAndroid if true (legacy signature), open the Android store, otherwise iOS
 */
function openMobileAppLink(targetPathOrIsAndroid, legacyIsAndroid) {
  const isLegacySignature =
    typeof targetPathOrIsAndroid === 'boolean' || targetPathOrIsAndroid === undefined;
  const isAndroid = isLegacySignature ? targetPathOrIsAndroid : legacyIsAndroid;

  if (!isLegacySignature && typeof targetPathOrIsAndroid === 'string') {
    const cleanedPath = targetPathOrIsAndroid.replace(/^https?:\/\/[^/]+/i, '').replace(/^\/+/, '');

    if (cleanedPath) {
      const appUrl = `${APP_SCHEME}://${cleanedPath}`;
      const storeUrl = getStoreUrl();
      let appTookOver = false;
      const startedAt = Date.now();

      const cleanup = () => {
        document.removeEventListener('visibilitychange', onVisibilityChange);
        window.removeEventListener('pagehide', markTakenOver);
      };

      const markTakenOver = () => {
        appTookOver = true;
      };

      const onVisibilityChange = () => {
        if (document.hidden) appTookOver = true;
      };

      // Register BEFORE the navigation so we never miss the hand-off event.
      document.addEventListener('visibilitychange', onVisibilityChange);
      window.addEventListener('pagehide', markTakenOver);

      // Try to open the app.
      window.location.href = appUrl;

      setTimeout(() => {
        cleanup();

        // The app launched and backgrounded us -> don't redirect.
        if (appTookOver || document.hidden) return;

        // JS was frozen while in the foreground -> the "Open in app?" dialog was
        // shown -> the app is installed (Open or Cancel) -> don't redirect.
        if (Date.now() - startedAt > FALLBACK_DELAY + SUSPEND_TOLERANCE) return;

        // Nothing took over and no dialog appeared -> the app isn't installed.
        // Same-tab redirect: window.open here would run outside the click's
        // user activation and get popup-blocked.
        window.location.href = storeUrl;
      }, FALLBACK_DELAY);

      return;
    }
  }

  if (isAndroid) {
    return window.open(PLAY_STORE_URL);
  }

  window.open(GET_THE_APP_URL);
}

export default openMobileAppLink;
